import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Dynamically load Razorpay checkout script
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function UpgradeDialog({ open, onOpenChange, onUpgraded }) {
  const { user, refresh: refreshAuth } = useAuth();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [canSimulate, setCanSimulate] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setCanSimulate(false);
    api.get("/subscriptions/plan").then((r) => setPlan(r.data));
  }, [open]);

  const finish = async () => {
    toast.success("Welcome to Pro!");
    await refreshAuth?.();
    onUpgraded?.();
    onOpenChange(false);
  };

  const upgrade = async () => {
    setBusy(true);
    setError("");
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Failed to load Razorpay checkout");
      const { data: order } = await api.post("/subscriptions/create-order");

      // Gateway not usable (bad keys in test mode) — offer simulation
      if (order?.ok === false || order?.razorpay_available === false) {
        setError(order?.detail || "Razorpay is not configured.");
        setCanSimulate(order?.razorpay_mode === "test");
        setBusy(false);
        return;
      }

      const rz = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "GEMS Pro",
        description: `${plan?.duration_days}-day Pro subscription`,
        order_id: order.order_id,
        prefill: { name: user?.name, email: user?.email, contact: user?.mobile || "" },
        theme: { color: "#0f172a" },
        handler: async (resp) => {
          try {
            await api.post("/subscriptions/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await finish();
          } catch (e) {
            toast.error(formatApiError(e));
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rz.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed"));
      rz.open();
    } catch (e) {
      setError(formatApiError(e));
      setBusy(false);
    }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      await api.post("/subscriptions/simulate-payment");
      await finish();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="heading text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" /> Upgrade to Pro
          </DialogTitle>
        </DialogHeader>
        {!plan ? (
          <div className="text-sm text-slate-500 py-4">Loading plan…</div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-2xl bg-slate-900 text-white p-6">
              <div className="text-xs uppercase tracking-widest font-semibold text-emerald-300">Pro plan</div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="amount text-4xl font-bold">₹{plan.price_inr}</span>
                <span className="text-sm text-slate-400">/ {plan.duration_days} days</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Unlimited groups</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Everything on Free</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Priority support</li>
              </ul>
              {plan.razorpay_mode === "test" && (
                <div className="mt-4 text-[11px] px-2 py-1 rounded-full bg-amber-400/20 text-amber-200 inline-block font-semibold">
                  TEST MODE · Use card 4111 1111 1111 1111
                </div>
              )}
            </div>
            <Button
              onClick={upgrade}
              disabled={busy}
              className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="upgrade-pay-button"
            >
              {busy ? "Opening Razorpay…" : `Pay ₹${plan.price_inr} & upgrade`}
            </Button>

            {error && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="upgrade-error-box">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-widest">Razorpay unavailable</div>
                <p className="text-xs text-amber-700 mt-1.5 leading-relaxed">{error}</p>
                {canSimulate && (
                  <Button
                    onClick={simulate}
                    disabled={busy}
                    variant="outline"
                    className="mt-3 h-10 rounded-full border-amber-300 text-amber-800 hover:bg-amber-100 w-full"
                    data-testid="upgrade-simulate-button"
                  >
                    {busy ? "Upgrading…" : "Complete with simulated test payment"}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
